/**
 * Route guard — gates the shell behind a signed-in Firebase user.
 * Optional `roles` narrows to specific roles (uses the Sprint-1 role stub).
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import type { Role } from '../lib/roles';

export default function RequireAuth({ roles }: { roles?: Role[] }) {
  const { user, role, loading, claimsResolving } = useAuth();

  if (loading) {
    return <div className="center-state">Restoring session…</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // BROWSER-RELOAD FIX (2026-06-05): on a hard reload the Firebase user is
  // known a tick BEFORE the team claims resolve, so `role` transiently reads
  // the `'client'` default for a real admin/staff user. A role-gated guard
  // that acted on that transient value redirected the user OFF a deep route
  // (e.g. /messages → "/") and stranded them on Home — the owner's "refresh
  // drops me to the home page" bug. So for a ROLE-GATED guard we wait until
  // claim resolution settles before trusting `role`. (An UN-gated guard does
  // not care about role, so it falls straight through and never blocks on
  // claims — keeps Home/Conversation instant.)
  if (roles && claimsResolving) {
    return <div className="center-state">Restoring session…</div>;
  }
  if (roles && role && !roles.includes(role)) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}
